import { INK, MUTED_36, MUTED_45, MUTED_60, FONT, ROOMS, glow, capsuleStyle } from "@/lib/yuoDesign";

/**
 * Chats history — frosted-glass sheet.
 *
 * Rules:
 *   • Semi-transparent dark surface with `backdrop-filter: blur(12px)` — never
 *     a solid black rectangle.
 *   • Only the MOST RECENT chat is rendered as a clean list item. Older ones
 *     stay accessible via a subtle "N older" counter that reveals them on tap.
 */
export function ConversationHistoryModal({ visible, onClose, items, activeId, accent, onSelect, onNew, onDelete }) {
  if (!visible) return null;
  const rgb = accent && accent.startsWith("#")
    ? [1, 3, 5].map((i) => parseInt(accent.slice(i, i + 2), 16)).join(",")
    : ROOMS.mirror.rgb;

  const sorted = [...(items || [])].sort((a, b) => {
    const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
    const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
    return tb - ta;
  });
  const primary = sorted[0];
  const rest = sorted.slice(1);

  return (
    <div className="yuo-modal-backdrop" onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 90,
        background: "var(--dk-3410-55)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="chat-history-modal"
        style={{
          position: "absolute", left: 0, right: 0, bottom: 0,
          maxHeight: "80%", overflow: "auto",
          background: `${glow(rgb, 0.10)}, var(--dk-101220-55)`,
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          borderTopLeftRadius: 32, borderTopRightRadius: 32,
          padding: 24,
          boxShadow: `0 -20px 60px rgba(${rgb},0.16)`,
          fontFamily: FONT, color: INK,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ color: INK, fontSize: 12, letterSpacing: "0.32em", textTransform: "uppercase" }}>
            Chats
          </div>
          <button onClick={onClose} data-testid="history-close"
            style={capsuleStyle({ active: false, size: "sm" })}>close</button>
        </div>

        <button
          onClick={() => { onNew(); onClose(); }}
          data-testid="history-new-chat"
          style={{
            width: "100%", padding: "14px 24px", borderRadius: 999, border: "none",
            background: `radial-gradient(120% 160% at 20% 0%, rgba(${rgb},0.34), rgba(${rgb},0.08))`,
            boxShadow: `0 0 34px rgba(${rgb},0.32)`,
            color: INK, fontFamily: FONT, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
            cursor: "pointer", marginBottom: 18,
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 12,
          }}>
          <span style={{
            width: 15, height: 15, borderRadius: "50%",
            background: `radial-gradient(38% 34% at 34% 26%, #fff, ${accent || ROOMS.mirror.hex} 20%, #05060c 100%)`,
            boxShadow: `0 0 14px ${accent || ROOMS.mirror.hex}`,
          }} />
          Start a new chat
        </button>

        {!primary ? (
          <div data-testid="history-empty"
            style={{ padding: 24, textAlign: "center", color: MUTED_36, fontSize: 12, letterSpacing: "0.24em", textTransform: "uppercase" }}>
            no chats yet
          </div>
        ) : (
          <>
            <div style={{ color: MUTED_45, fontSize: 9.5, letterSpacing: "0.28em", textTransform: "uppercase", marginBottom: 8 }}>
              Most recent
            </div>
            <ChatRow item={primary} activeId={activeId} rgb={rgb} onSelect={onSelect} onClose={onClose} onDelete={onDelete} />
            {rest.length > 0 && (
              <details style={{ marginTop: 14 }} data-testid="history-older">
                <summary style={{
                  listStyle: "none",
                  cursor: "pointer",
                  padding: "10px 16px", borderRadius: 999,
                  background: "var(--fg-04)",
                  color: MUTED_60, fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase",
                  display: "inline-flex", alignItems: "center", gap: 10,
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: `radial-gradient(38% 34% at 34% 26%, #fff, ${accent || ROOMS.mirror.hex} 20%, #05060c 100%)`,
                    boxShadow: `0 0 8px rgba(${rgb},0.6)`,
                  }} />
                  {rest.length} older
                </summary>
                <div style={{ marginTop: 10 }}>
                  {rest.map((item) => (
                    <ChatRow key={item.conversation_id} item={item} activeId={activeId} rgb={rgb}
                      onSelect={onSelect} onClose={onClose} onDelete={onDelete} muted />
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ChatRow({ item, activeId, rgb, onSelect, onClose, onDelete, muted }) {
  const active = activeId === item.conversation_id;
  return (
    <div
      data-testid={`history-row-${item.conversation_id}`}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "14px 18px", borderRadius: 22, marginTop: 8,
        background: active
          ? glow(rgb, 0.20)
          : muted ? "var(--fg-03)" : "var(--fg-05)",
        boxShadow: active ? `0 0 22px rgba(${rgb},0.20)` : "none",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}>
      <button style={{
          flex: 1, textAlign: "left", background: "transparent", border: "none",
          color: INK, fontFamily: FONT, cursor: "pointer", padding: 0,
        }}
        onClick={() => { onSelect(item.conversation_id); onClose(); }}
        data-testid={`history-select-${item.conversation_id}`}>
        <div style={{
          color: INK, fontSize: 13.5,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{item.title || "New chat"}</div>
        {!!item.updated_at && (
          <div style={{ color: MUTED_45, fontSize: 10.5, marginTop: 4, letterSpacing: 0.16 }}>
            {new Date(item.updated_at).toLocaleDateString()} ·{" "}
            {new Date(item.updated_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
          </div>
        )}
      </button>
      {onDelete && (
        <button onClick={() => onDelete(item.conversation_id)}
          data-testid={`history-delete-${item.conversation_id}`}
          style={capsuleStyle({ active: false, size: "sm", rgb: "255,122,102" })}>
          delete
        </button>
      )}
    </div>
  );
}
